import Phaser from "phaser";
import { PLAYER_DISPLAY_HEIGHT_PX } from "@/character/skills/constants";
import type { CharacterGridSpec } from "@/character/grid";
import type { CharacterProgression } from "@/game/model/character-progression";
import type { StatAllocation } from "@/game/model/character-stats";
import type { HomeSkillRuntimePayload } from "@/home/home-events";
import type { HomeRoomData } from "@/home/storage";
import { createGameConfig, createHomeGameConfig } from "@/game/config";
import { SceneKeys } from "@/game/data/scene-keys";
import { BootScene } from "@/game/scenes/BootScene";
import { GameScene } from "@/game/scenes/GameScene";
import { HomeRoomScene } from "@/game/scenes/HomeRoomScene";
import { PreloadScene } from "@/game/scenes/PreloadScene";
import { TowerScene } from "@/game/scenes/TowerScene";
import type { TowerFloorBattlePlan } from "@/tower/tower-floor-runtime";
import type { LeaderboardBoard, RankBadgeConfigBoard } from "@/rank/leaderboard-types";
import type { PetDefinition } from "@/pet/types";

export type GameArtOptions = {
  playerIdleTextureUrl?: string;
  playerSpritesheetUrl?: string;
  playerSpritesheetGrid?: CharacterGridSpec;
  shadowIdleTextureUrl?: string;
  shadowSpritesheetUrl?: string;
  shadowSpritesheetGrid?: CharacterGridSpec;
  /** Raid visit — defender's public equipped shadow. */
  defenderShadowIdleTextureUrl?: string;
  defenderShadowSpritesheetUrl?: string;
  defenderShadowSpritesheetGrid?: CharacterGridSpec;
  petIdleTextureUrl?: string;
  petSpritesheetUrl?: string;
  petSpritesheetGrid?: CharacterGridSpec;
};

function applyArtToRegistry(game: Phaser.Game, art?: GameArtOptions): void {
  game.registry.set("playerDisplayHeightPx", PLAYER_DISPLAY_HEIGHT_PX);
  if (art?.playerIdleTextureUrl) {
    game.registry.set("playerIdleTextureUrl", art.playerIdleTextureUrl);
  }
  if (art?.playerSpritesheetUrl) {
    game.registry.set("playerSpritesheetUrl", art.playerSpritesheetUrl);
  }
  if (art?.playerSpritesheetGrid) {
    game.registry.set("playerSpritesheetGrid", art.playerSpritesheetGrid);
  }
  if (art?.shadowIdleTextureUrl) {
    game.registry.set("shadowIdleTextureUrl", art.shadowIdleTextureUrl);
  }
  if (art?.shadowSpritesheetUrl) {
    game.registry.set("shadowSpritesheetUrl", art.shadowSpritesheetUrl);
  }
  if (art?.shadowSpritesheetGrid) {
    game.registry.set("shadowSpritesheetGrid", art.shadowSpritesheetGrid);
  }
  if (art?.defenderShadowIdleTextureUrl) {
    game.registry.set("defenderShadowIdleTextureUrl", art.defenderShadowIdleTextureUrl);
  }
  if (art?.defenderShadowSpritesheetUrl) {
    game.registry.set("defenderShadowSpritesheetUrl", art.defenderShadowSpritesheetUrl);
  }
  if (art?.defenderShadowSpritesheetGrid) {
    game.registry.set("defenderShadowSpritesheetGrid", art.defenderShadowSpritesheetGrid);
  }
  if (art?.petIdleTextureUrl) {
    game.registry.set("petIdleTextureUrl", art.petIdleTextureUrl);
  }
  if (art?.petSpritesheetUrl) {
    game.registry.set("petSpritesheetUrl", art.petSpritesheetUrl);
  }
  if (art?.petSpritesheetGrid) {
    game.registry.set("petSpritesheetGrid", art.petSpritesheetGrid);
  }
}

export type PhaserBootOptions = {
  homeData?: HomeRoomData;
  skillRuntime?: HomeSkillRuntimePayload;
  progression?: CharacterProgression;
  equipmentStatBonus?: Partial<StatAllocation>;
  towerFloor?: number;
  towerBattlePlan?: TowerFloorBattlePlan;
  towerResize?: boolean;
  /** Layout-only editor: hide player, always show placement grid. */
  homeRoomDesignLayout?: boolean;
  /** Camera zoom in design-layout editor (1 = 100%). Grid cell size unchanged. */
  homeRoomEditorZoom?: number;
  rankBadgeBoards?: LeaderboardBoard[];
  rankBadgeConfigs?: RankBadgeConfigBoard[];
  combatManaCurrent?: number | null;
  homeStructureOwnerUid?: string;
  homeStructureCombat?: {
    maxHp: number;
    defense: number;
    currentHp: number;
    solidPlacementId: string | null;
    raidMode: boolean;
    allowAttack: boolean;
    defenderUid?: string;
    sessionBaseDamage?: number;
  };
  equippedPet?: PetDefinition | null;
  petSkillRuntime?: HomeSkillRuntimePayload;
};

function startPhaserGame(
  parent: HTMLElement,
  scenes: Phaser.Types.Scenes.SceneType[],
  startSceneKey: string,
  art?: GameArtOptions,
  boot?: PhaserBootOptions,
): Phaser.Game {
  const buildConfig =
    boot?.homeData || boot?.towerResize ? createHomeGameConfig : createGameConfig;
  const config = buildConfig(parent, scenes, {
    callbacks: {
      preBoot: (game) => {
        game.registry.set("startSceneKey", startSceneKey);
        applyArtToRegistry(game, art);
        if (boot?.homeData) {
          game.registry.set("homeRoomData", boot.homeData);
        }
        if (boot?.skillRuntime) {
          game.registry.set("homeSkillRuntime", boot.skillRuntime);
        }
        if (boot?.progression) {
          game.registry.set("characterProgression", boot.progression);
        }
        if (boot?.equipmentStatBonus) {
          game.registry.set("equipmentStatBonus", boot.equipmentStatBonus);
        }
        if (boot?.towerFloor != null) {
          game.registry.set("towerFloor", boot.towerFloor);
        }
        if (boot?.towerBattlePlan) {
          game.registry.set("towerBattlePlan", boot.towerBattlePlan);
        }
        if (boot?.homeRoomDesignLayout) {
          game.registry.set("homeRoomDesignLayout", true);
        }
        if (boot?.homeRoomEditorZoom != null && boot.homeRoomEditorZoom > 0) {
          game.registry.set("homeRoomEditorZoom", boot.homeRoomEditorZoom);
        }
        if (boot?.rankBadgeBoards) {
          game.registry.set("rankBadgeBoards", boot.rankBadgeBoards);
        }
        if (boot?.rankBadgeConfigs) {
          game.registry.set("rankBadgeConfigs", boot.rankBadgeConfigs);
        }
        if (boot?.combatManaCurrent !== undefined) {
          game.registry.set("combatManaCurrent", boot.combatManaCurrent);
        }
        if (boot?.homeStructureOwnerUid) {
          game.registry.set("homeStructureOwnerUid", boot.homeStructureOwnerUid);
        }
        if (boot?.homeStructureCombat) {
          game.registry.set("homeStructureCombat", boot.homeStructureCombat);
        }
        if (boot?.equippedPet) {
          game.registry.set("equippedPet", boot.equippedPet);
        }
        if (boot?.petSkillRuntime) {
          game.registry.set("petSkillRuntime", boot.petSkillRuntime);
        }
      },
    },
  });
  return new Phaser.Game(config);
}

export function startGame(parent: HTMLElement, art?: GameArtOptions): Phaser.Game {
  return startPhaserGame(
    parent,
    [BootScene, PreloadScene, GameScene],
    SceneKeys.Game,
    art,
  );
}

export type HomeRoomBootOptions = PhaserBootOptions & {
  homeData: HomeRoomData;
};

export function startHomeRoom(
  parent: HTMLElement,
  art?: GameArtOptions,
  home?: HomeRoomBootOptions,
): Phaser.Game {
  return startPhaserGame(
    parent,
    [BootScene, PreloadScene, HomeRoomScene],
    SceneKeys.HomeRoom,
    art,
    home,
  );
}

export type TowerBootOptions = {
  towerFloor: number;
  progression?: CharacterProgression;
  equipmentStatBonus?: Partial<StatAllocation>;
  towerBattlePlan?: TowerFloorBattlePlan;
  skillRuntime?: HomeSkillRuntimePayload;
  rankBadgeBoards?: LeaderboardBoard[];
  rankBadgeConfigs?: RankBadgeConfigBoard[];
  combatManaCurrent?: number | null;
  equippedPet?: PetDefinition | null;
  petSkillRuntime?: HomeSkillRuntimePayload;
};

export function startTowerBattle(
  parent: HTMLElement,
  art?: GameArtOptions,
  tower?: TowerBootOptions,
): Phaser.Game {
  const inTowerModalBattle = Boolean(parent.closest(".tower-modal.tower-modal--battle"));
  const useResizeScale =
    !parent.closest(".dungeon-trial-modal") && !inTowerModalBattle;
  return startPhaserGame(
    parent,
    [BootScene, PreloadScene, TowerScene],
    SceneKeys.Tower,
    art,
    {
      progression: tower?.progression,
      equipmentStatBonus: tower?.equipmentStatBonus,
      towerFloor: tower?.towerFloor,
      towerBattlePlan: tower?.towerBattlePlan,
      skillRuntime: tower?.skillRuntime,
      rankBadgeBoards: tower?.rankBadgeBoards,
      rankBadgeConfigs: tower?.rankBadgeConfigs,
      combatManaCurrent: tower?.combatManaCurrent,
      equippedPet: tower?.equippedPet,
      petSkillRuntime: tower?.petSkillRuntime,
      towerResize: useResizeScale,
    },
  );
}
